import React from "react";
import { Box, Tabs, Tab } from "@mui/material";

interface TabItem {
  id: number;
  label: string;
}

interface PropertyRegistrationTabsProps {
  currentTab: number;
  tabs: TabItem[];
  onTabChange: (tabId: number) => void;
  children?: React.ReactNode;
  disableConfirm?: boolean;
  expandTabBackground?: boolean;
  hasFixedActionBar?: boolean;
  /**
   * true にすると、タブ枠(ヘッダー)はそのまま固定され、
   * タブコンテンツ領域だけが内部スクロールするようになる。
   * 親要素側で固定高さを与える必要がある (例: height: calc(100vh - X))。
   */
  scrollableContent?: boolean;
}

const PropertyRegistrationTabs: React.FC<PropertyRegistrationTabsProps> = ({
  currentTab,
  tabs,
  onTabChange,
  children,
  disableConfirm = false,
  expandTabBackground = false,
  hasFixedActionBar = false,
  scrollableContent = false,
}) => {
  // スクロール位置に応じて上端フェードオーバーレイの表示を切り替える
  const [isScrolled, setIsScrolled] = React.useState(false);
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const next = e.currentTarget.scrollTop > 0;
    setIsScrolled((prev) => (prev !== next ? next : prev));
  }, []);

  // タブ切り替え時はスクロール位置がリセットされるためオーバーレイを消す
  React.useEffect(() => {
    setIsScrolled(false);
  }, [currentTab]);

  return (
    <Box
      sx={{
        backgroundColor: "transparent",
        width: "100%",
        ...(scrollableContent && {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }),
      }}
    >
      {/* タブヘッダー */}
      <Tabs
        value={currentTab}
        onChange={(_, newValue) => onTabChange(newValue)}
        sx={{
          backgroundColor: expandTabBackground ? "#F5F5F5" : "transparent",
          minHeight: "36px",
          flexShrink: 0,
          "& .MuiTabs-indicator": {
            display: "none",
          },
          "& .MuiTabs-flexContainer": {
            gap: "6px",
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            label={tab.label}
            value={tab.id}
            disableRipple
            data-no-confirm={disableConfirm ? "true" : undefined}
            sx={{
              fontFamily: '"Noto Sans JP", sans-serif',
              fontWeight: 700,
              fontSize: "14px",
              color: "#989898",
              textTransform: "none",
              minHeight: "41px",
              padding: "10px 12px 4px 12px",
              border: "none",
              "&.Mui-selected": {
                color: "#0B9DBD",
                backgroundColor: "white",
                borderRadius: "8px 8px 0 0",
              },
              "&:hover": {
                color: "#0B9DBD",
                backgroundColor: "transparent",
                opacity: 1,
              },
              "&.Mui-selected:hover": {
                backgroundColor: "white",
              },
            }}
          />
        ))}
      </Tabs>

      {/* タブコンテンツ */}
      {scrollableContent ? (
        // スクロールするコンテンツ枠。上端に白いフェードオーバーレイを重ねて
        // タブとコンテンツのぶつ切り感を緩和する。
        <Box
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            backgroundColor: "white",
            borderRadius: currentTab === 0 ? "0 8px 8px 8px" : "8px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* 上端フェードオーバーレイ (スクロール時のみ表示) */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "20px",
              pointerEvents: "none",
              zIndex: 2,
              background:
                "linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)",
              borderTopLeftRadius: currentTab === 0 ? "0px" : "inherit",
              borderTopRightRadius: "inherit",
              opacity: isScrolled ? 1 : 0,
              transition: "opacity 0.15s ease-out",
            }}
          />
          <Box
            onScroll={handleScroll}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflowX: "hidden",
              overflowY: "auto",
              padding: "14px",
              // FixedActionBar (position:fixed bottom:0, 高さ約46px) と
              // 最下端のコンテンツが被らないようバッファを確保する
              ...(hasFixedActionBar ? { paddingBottom: "60px" } : {}),
              borderRadius: "inherit",
            }}
          >
            {children}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            padding: "14px",
            backgroundColor: "white",
            borderRadius: currentTab === 0 ? "0 8px 8px 8px" : "8px",
            minHeight: hasFixedActionBar ? "calc(100vh - 351px)" : "auto",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

export default PropertyRegistrationTabs;
